/**
 * Money for the Payroll Accounting Export screens is held as INTEGER CENTS and never as a floating point amount.
 *
 * The API sends amounts as JSON numbers with exactly the stored precision (two decimals), and `JSON.parse` turns them into
 * doubles. A double is only ever read through its decimal text here (never multiplied by 100), so 0.1 + 0.2 style drift cannot
 * reach a total or a comparison. Every sum, difference and comparison on this page is integer arithmetic on cents.
 */
export type Cents = number;

/**
 * Parses a decimal amount ("1234.50", "-0.07", or a JSON number) into integer cents without floating point arithmetic.
 * More than two decimals are rounded half away from zero on the digits (the API never sends more; a value with more is shown rather
 * than hidden). Anything that is not a plain decimal number is `NaN` so a bad value is never silently shown as zero.
 */
export function toCents(value: number | string | null | undefined): Cents {
  if (value === null || value === undefined) return Number.NaN;
  let text: string;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return Number.NaN;
    // toFixed(8) gives the exact decimal text of the double to far more digits than money ever carries, so the digits below are
    // the number's own digits; no multiplication is involved.
    text = value.toFixed(8);
  } else {
    text = value.trim();
  }
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(text);
  if (!match) return Number.NaN;
  const negative = match[1] === '-';
  const whole = match[2]!;
  const fraction = (match[3] ?? '').padEnd(3, '0');
  let cents = Number(whole) * 100 + Number(fraction.slice(0, 2));
  if (Number(fraction[2]) >= 5) cents += 1;
  return negative && cents !== 0 ? -cents : cents;
}

/** Like `toCents` but a missing or unreadable value is zero. Use only where an absent figure really means none (never for a total). */
export function toCentsOrZero(value: number | string | null | undefined): Cents {
  const cents = toCents(value);
  return Number.isNaN(cents) ? 0 : cents;
}

export function sumCents(values: readonly Cents[]): Cents {
  let total = 0;
  for (const v of values) total += v;
  return total;
}

/** "1,234.56" (no currency sign), "-0.07", "0.00". Integer arithmetic only. */
export function formatCents(cents: Cents): string {
  if (!Number.isFinite(cents)) return '';
  const negative = cents < 0;
  const abs = Math.abs(Math.trunc(cents));
  const whole = Math.floor(abs / 100);
  const fraction = String(abs % 100).padStart(2, '0');
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}.${fraction}`;
}

/** "$1,234.56" or "-$0.07". */
export function formatUsd(cents: Cents): string {
  const text = formatCents(cents);
  if (!text) return '';
  return text.startsWith('-') ? `-$${text.slice(1)}` : `$${text}`;
}

/** The decimal text the CSV uses for an amount: exactly two decimals, `.` separator, no grouping. */
export function plainDecimal(cents: Cents): string {
  return formatCents(cents).replace(/,/g, '');
}
